import React, {useEffect, useMemo} from 'react';
import PropTypes from 'prop-types';
import {Checkbox, Divider, Space} from 'antd';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {
  StyledApprovalsTab,
  StyledApprovalsTabSpace,
  StyledApprovalsTabReviewsCount,
  StyledApprovalsTabActionsSpace
} from './StyledApprovalsTab';
import UserReview from '../../../../UserReview';
import {dateHelpers, expensesHelpers} from '../../../../../utils/helpers';


const ApprovalsTab = ({
  dispatch,
  employeeEmail,
  isAdmin,

  expense,
  onApprove,
  onReassign,
  onReject,
  onReviewEdit,
  ...rest
}) => {
  const [t] = useTranslation('main');
  const [isOverrideChecked, setIsOverrideChecked] = React.useState(false);

  const reviewButtonProps = { disabled: !isOverrideChecked };

  const isAlreadyAdminApproved = useMemo(() => {
    return expensesHelpers.isExpenseAlreadyApprovedByAdmin(expense);
  }, [expense?.approval]); // eslint-disable-line react-hooks/exhaustive-deps

  const reviews = useMemo(() => expensesHelpers.getExpenseApprovals(expense), [expense?.approval]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAdminApprovalAvailable = useMemo(() => {
    const isInReviewStatus = expensesHelpers.isExpenseInReview(expense);
    const isInApprovalList = expensesHelpers.isEmployeeInExpenseApprovalList(expense, employeeEmail);
    const isAlreadyApproved = expensesHelpers.isExpenseAlreadyApprovedByEmployee(expense, employeeEmail);
    const isAnyAdminApproval = expensesHelpers.isAnyAdminApproval(expense);

    if (isAdmin && isAnyAdminApproval) return true;
    return isAdmin && isInReviewStatus && !isInApprovalList && !isAlreadyApproved;
  }, [isAdmin, employeeEmail, expense?.approval, expense?.review_status]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isOverrideChecked) setIsOverrideChecked(false);
  }, [expense]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleApprove = (e, override = false) => onApprove?.({e, expense, data: { admin_override: override }});

  const handleReject = (e, override = false) => onReject?.({e, expense, data: { admin_override: override }});

  const handleReassign = (e, value) => onReassign?.(e, { current_approver: employeeEmail, new_approver: value });

  const renderReview = (review, idx) => {
    const isApproved = review?.is_approved;
    const date = dateHelpers.getDateFromISO(review.approval_date, 'DD MMM YYYY, HH:mm');
    const canEdit = review?.approver?.email === employeeEmail;
    const status = isApproved === true
      ? 'approved' : isApproved === false ? 'rejected' : canEdit ? 'pending' : 'default';

    return (
      <React.Fragment key={review.id}>
        <UserReview
          date={date}
          canEdit={canEdit}
          canReassign={canEdit}
          message={review.comment}
          reviewer={review.approver}
          status={status}
          onApprove={(e) => onApprove({e, expense})}
          onEdit={onReviewEdit}
          onReject={(e) => onReject({e, expense})}
          onReassign={handleReassign}
        />
        {idx < reviews.length - 1 && <Divider style={{ margin: '10px 0', width: 32 }} />}
      </React.Fragment>
    );
  }

  return (
    <StyledApprovalsTab {...rest}>
      {isAlreadyAdminApproved && (
        <Space className='w-100' direction='vertical' size='middle'>
          <Checkbox checked disabled>{t('adminOverride')}</Checkbox>
          {reviews.filter(r => r.admin_override).map(renderReview)}
        </Space>
      )}
      {!isAlreadyAdminApproved && (
        <StyledApprovalsTabSpace direction='vertical' size={10}>
          {reviews.length > 1 && (
            <StyledApprovalsTabReviewsCount>
              {t('parallelReviews')} ({reviews.length})
            </StyledApprovalsTabReviewsCount>
          )}
          <StyledApprovalsTabSpace direction='vertical' size={0}>
            {reviews.map(renderReview)}
          </StyledApprovalsTabSpace>
          {isAdminApprovalAvailable && (
            <React.Fragment>
              <Divider style={{ margin: '14px 0' }} />
              <StyledApprovalsTabActionsSpace size='middle' direction='vertical'>
                <Checkbox checked={isOverrideChecked} onChange={() => setIsOverrideChecked(!isOverrideChecked)}>
                  {t('adminOverride')}
                </Checkbox>
                <UserReview.ReviewButtons
                  approveButtonProps={{ ...reviewButtonProps, onClick: (e) => handleApprove(e, true) }}
                  className='approvals-tab-review-buttons'
                  rejectButtonProps={{ ...reviewButtonProps, onClick: (e) => handleReject(e, true) }}
                />
              </StyledApprovalsTabActionsSpace>
            </React.Fragment>
          )}
        </StyledApprovalsTabSpace>
      )}
    </StyledApprovalsTab>
  );
}

ApprovalsTab.propTypes = {
  expense: PropTypes.object,
  onApprove: PropTypes.func,
  onReassign: PropTypes.func,
  onReject: PropTypes.func,
  onReviewEdit: PropTypes.func,
}

const mapStateToProps = ({ user }) => ({
  employeeEmail: user?.employee?.email,
  isAdmin: user.isAdmin,
});

export default connect(mapStateToProps, null)(ApprovalsTab);
